// @generated-imports
import { escapeAttribute, escapeHTML, findGameById, openGameModal, showToast, truncate } from './app.js';
import { loadLastPlayed } from './autosave.js';
import { loadCollections } from './collections.js';
import { CONFIG, dom, state } from './core.js';
import { layerClosed, layerOpened } from './layers.js';
import { formatPlayTime } from './platform.js';
import { MAIN_PROFILE_ID, profileName } from './profiles.js';
import { relativeTime } from './resume.js';
// @generated-imports
/* ===========================================================================
   ESTATÍSTICAS e CONQUISTAS LOCAIS (v2.1)
   ---------------------------------------------------------------------------
   Tudo derivado de dados que já existem (tempo e contagem por jogo, último
   jogo, favoritos) mais contadores simples em `emutab:counters`. Conquistas
   são marcos do próprio portal — sem conta, sem servidor. Por perfil.
   ======================================================================== */

function loadCounters() {
  try { return JSON.parse(localStorage.getItem(CONFIG.localStorageCountersKey) || '{}') || {}; } catch (_) { return {}; }
}

function saveCounters(counters) {
  try { localStorage.setItem(CONFIG.localStorageCountersKey, JSON.stringify(counters)); } catch (_) {}
}

function bumpCounter(name, by = 1) {
  const counters = loadCounters();
  counters[name] = (Number(counters[name]) || 0) + by;
  saveCounters(counters);
  return counters[name];
}

function setCounterMax(name, value) {
  const counters = loadCounters();
  if ((Number(value) || 0) > (Number(counters[name]) || 0)) {
    counters[name] = Number(value);
    saveCounters(counters);
  }
}

function computeStats() {
  const byId = new Map(state.roms.map((game) => [game.id, game]));
  const lastPlayed = loadLastPlayed();
  const ids = new Set([...Object.keys(state.playTime || {}), ...Object.keys(state.playCounts || {})]);
  const bySystem = {};
  const byGenre = {};
  let total = 0;
  const games = [];
  ids.forEach((id) => {
    const seconds = Number(state.playTime[id]) || 0;
    const count = Number(state.playCounts[id]) || 0;
    const game = byId.get(id);
    if (!game || (!seconds && !count)) return;
    total += seconds;
    bySystem[game.system] = (bySystem[game.system] || 0) + seconds;
    byGenre[game.genre] = (byGenre[game.genre] || 0) + seconds;
    games.push({ id, title: game.title, system: game.system, seconds, count, lastPlayed: lastPlayed[id] || 0 });
  });
  const catalogSystems = new Set((state.catalog || []).map((g) => g.system));
  const systemsPlayed = new Set(games.filter((g) => catalogSystems.has(g.system)).map((g) => g.system));
  const now = Date.now();
  const staleFavorites = [...state.favorites]
    .map((id) => byId.get(id)).filter(Boolean)
    .map((game) => ({ id: game.id, title: game.title, days: lastPlayed[game.id] ? Math.floor((now - lastPlayed[game.id]) / 86400000) : null }))
    .filter((f) => f.days === null || f.days >= 14)
    .sort((a, b) => (b.days === null ? 1e9 : b.days) - (a.days === null ? 1e9 : a.days));
  const last = games.slice().sort((a, b) => b.lastPlayed - a.lastPlayed)[0] || null;
  return {
    totalSeconds: total,
    gamesPlayed: games.length,
    bySystem,
    byGenre,
    topByTime: games.slice().sort((a, b) => b.seconds - a.seconds).slice(0, 5),
    topByCount: games.slice().sort((a, b) => b.count - a.count).slice(0, 5),
    systemsPlayed: systemsPlayed.size,
    catalogSystems: catalogSystems.size,
    staleFavorites,
    lastGame: last && last.lastPlayed ? last : null,
    favorites: state.favorites.size,
    collections: loadCollections().length,
    counters: loadCounters(),
  };
}

const ACHIEVEMENTS = [
  { id: 'first-play', icon: '🕹️', title: 'Primeiro cartucho', desc: 'Jogou pela primeira vez.', test: (s) => s.gamesPlayed >= 1 },
  { id: 'five-games', icon: '🧭', title: 'Explorador', desc: 'Jogou 5 jogos diferentes.', test: (s) => s.gamesPlayed >= 5 },
  { id: 'twenty-games', icon: '📦', title: 'Colecionador', desc: 'Jogou 20 jogos diferentes.', test: (s) => s.gamesPlayed >= 20 },
  { id: 'all-systems', icon: '🌎', title: 'Multiplataforma', desc: 'Jogou em todos os sistemas do catálogo.', test: (s) => s.catalogSystems > 0 && s.systemsPlayed >= s.catalogSystems },
  { id: 'one-hour', icon: '⏱️', title: 'Uma hora de fita', desc: '1 hora de jogo no total.', test: (s) => s.totalSeconds >= 3600 },
  { id: 'ten-hours', icon: '🏃', title: 'Maratonista', desc: '10 horas de jogo no total.', test: (s) => s.totalSeconds >= 36000 },
  { id: 'long-session', icon: '🍿', title: 'Sem pausa pro lanche', desc: 'Uma sessão de 2 horas seguidas.', test: (s) => (Number(s.counters.longestSessionMin) || 0) >= 120 },
  { id: 'first-state', icon: '💾', title: 'Salvo pelo gongo', desc: 'Salvou o primeiro estado.', test: (s) => (Number(s.counters.stateSaves) || 0) >= 1 },
  { id: 'fifty-states', icon: '🛟', title: 'Precavido', desc: '50 estados salvos.', test: (s) => (Number(s.counters.stateSaves) || 0) >= 50 },
  { id: 'multi', icon: '🤝', title: 'Dupla dinâmica', desc: 'Jogou com dois ou mais controles.', test: (s) => (Number(s.counters.multiSessions) || 0) >= 1 },
  { id: 'favorites', icon: '❤️', title: 'Tenho meus preferidos', desc: '5 jogos favoritos.', test: (s) => s.favorites >= 5 },
  { id: 'offline', icon: '✈️', title: 'Pronto pra viagem', desc: 'Deixou um jogo disponível offline.', test: (s) => (Number(s.counters.offlineGames) || 0) >= 1 },
  { id: 'collection', icon: '📚', title: 'Curador', desc: 'Criou uma coleção.', test: (s) => s.collections >= 1 },
  { id: 'local-rom', icon: '📼', title: 'Do meu baú', desc: 'Jogou uma ROM do próprio aparelho.', test: (s) => (Number(s.counters.localPlays) || 0) >= 1 },
];

function loadAchievements() {
  try { return JSON.parse(localStorage.getItem(CONFIG.localStorageAchievementsKey) || 'null'); } catch (_) { return null; }
}

/** Avalia e registra conquistas novas. Na primeira avaliação (quem vem da
 *  2.0 já com histórico) registra em silêncio, sem enxurrada de toasts. */
function evaluateAchievements() {
  const stored = loadAchievements();
  const silent = stored === null;
  const unlocked = stored || {};
  const stats = computeStats();
  const fresh = ACHIEVEMENTS.filter((a) => !unlocked[a.id] && a.test(stats));
  if (!fresh.length && !silent) return [];
  fresh.forEach((a) => { unlocked[a.id] = Date.now(); });
  try { localStorage.setItem(CONFIG.localStorageAchievementsKey, JSON.stringify(unlocked)); } catch (_) {}
  if (!silent && fresh.length) {
    showToast(fresh.length === 1
      ? `🏆 Conquista: ${fresh[0].icon} ${fresh[0].title} — ${fresh[0].desc}`
      : `🏆 ${fresh.length} conquistas novas: ${fresh.map((a) => `${a.icon} ${a.title}`).join(', ')}`);
  }
  return fresh;
}

function statBars(map) {
  const entries = Object.entries(map).sort((a, b) => b[1] - a[1]);
  const max = entries.length ? entries[0][1] || 1 : 1;
  return entries.map(([label, seconds]) => `<div class="stat-bar">
      <span class="stat-bar-label">${escapeHTML(label)}</span>
      <span class="stat-bar-track"><span class="stat-bar-fill" style="width:${Math.max(4, Math.round((seconds / max) * 100))}%"></span></span>
      <span class="stat-bar-value">${formatPlayTime(seconds)}</span>
    </div>`).join('') || '<p class="backup-note">Ainda sem tempo registrado.</p>';
}

function openStatsPanel() {
  if (!dom.statsModal) return;
  const stats = computeStats();
  const unlocked = loadAchievements() || {};
  const list = (games, field) => games.length
    ? `<ol class="stat-list">${games.map((g) => `<li><button class="link-btn" type="button" data-stats-open="${escapeAttribute(g.id)}">${escapeHTML(truncate(g.title, 34))}</button> <small>${field === 'count' ? `${g.count}×` : formatPlayTime(g.seconds)}</small></li>`).join('')}</ol>`
    : '<p class="backup-note">Nada por aqui ainda.</p>';
  const stale = stats.staleFavorites.slice(0, 5).map((f) => `<li><button class="link-btn" type="button" data-stats-open="${escapeAttribute(f.id)}">${escapeHTML(truncate(f.title, 34))}</button> <small>${f.days === null ? 'nunca jogado' : `há ${f.days} dias`}</small></li>`).join('');
  dom.statsBody.innerHTML = `
    <div class="stat-cards">
      <div class="stat-card"><strong>${formatPlayTime(stats.totalSeconds)}</strong><small>tempo total</small></div>
      <div class="stat-card"><strong>${stats.gamesPlayed}</strong><small>jogos jogados</small></div>
      <div class="stat-card"><strong>${stats.systemsPlayed}/${stats.catalogSystems}</strong><small>sistemas</small></div>
      <div class="stat-card"><strong>${Number(stats.counters.stateSaves) || 0}</strong><small>estados salvos</small></div>
    </div>
    ${stats.lastGame ? `<p class="backup-note">Último jogado: <strong>${escapeHTML(stats.lastGame.title)}</strong> — ${escapeHTML(relativeTime(stats.lastGame.lastPlayed))}.</p>` : ''}
    <h4 class="backup-subtitle">Por sistema</h4>${statBars(stats.bySystem)}
    <h4 class="backup-subtitle">Por gênero</h4>${statBars(stats.byGenre)}
    <div class="stat-columns">
      <div><h4 class="backup-subtitle">Mais tempo</h4>${list(stats.topByTime, 'time')}</div>
      <div><h4 class="backup-subtitle">Mais vezes</h4>${list(stats.topByCount, 'count')}</div>
    </div>
    ${stale ? `<h4 class="backup-subtitle">Favoritos esquecidos</h4><ol class="stat-list">${stale}</ol>` : ''}
    <h4 class="backup-subtitle">🏆 Conquistas (${Object.keys(unlocked).length}/${ACHIEVEMENTS.length})</h4>
    <div class="achievement-grid">${ACHIEVEMENTS.map((a) => `<div class="achievement${unlocked[a.id] ? ' is-unlocked' : ''}" title="${escapeAttribute(a.desc)}">
        <span class="achievement-icon" aria-hidden="true">${unlocked[a.id] ? a.icon : '🔒'}</span>
        <span><strong>${escapeHTML(a.title)}</strong><small>${escapeHTML(a.desc)}${unlocked[a.id] ? ` · ${new Date(unlocked[a.id]).toLocaleDateString('pt-BR')}` : ''}</small></span>
      </div>`).join('')}</div>`;
  dom.statsTitle.textContent = `📊 Estatísticas — ${profileName(state.profileId || MAIN_PROFILE_ID)}`;
  dom.statsModal.classList.remove('is-hidden');
  layerOpened('stats', dom.statsClose);
}

function closeStatsPanel() {
  if (!dom.statsModal) return;
  dom.statsModal.classList.add('is-hidden');
  layerClosed('stats');
}

function handleStatsClick(event) {
  const open = event.target.closest('[data-stats-open]');
  if (!open) return;
  const game = findGameById(open.dataset.statsOpen);
  if (!game) return;
  closeStatsPanel();
  openGameModal(game);
}

// @generated-exports
export { ACHIEVEMENTS, bumpCounter, closeStatsPanel, computeStats, evaluateAchievements, handleStatsClick, loadAchievements, loadCounters, openStatsPanel, saveCounters, setCounterMax, statBars };
// @generated-exports
